import React, { useState } from 'react';
import { motion } from 'framer-motion';

const OnboardingFlow = ({ onClose }) => {
  const [step, setStep] = useState(1);
  const [focus, setFocus] = useState('');
  const [company, setCompany] = useState('');
  
  const handleNext = () => {
    setStep(step + 1);
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <motion.div 
        className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 relative"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
      >
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          aria-label="Close"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        
        {step === 1 && (
          <div className="text-center">
            <div className="mb-6">
              <div className="mx-auto w-20 h-20">
                <svg viewBox="0 0 100 100" className="w-full h-full">
                  <path 
                    d="M35,70 C35,60 65,60 65,70 L65,85 C65,90 35,90 35,85 Z" 
                    fill="#0A1A2F" 
                    className="drop-shadow-md"
                  />
                  <path 
                    d="M40,70 L40,60 C40,45 60,45 60,60 L60,70" 
                    fill="#0A1A2F" 
                    stroke="#D4AF37" 
                    strokeWidth="2"
                  />
                  <path 
                    d="M45,45 C45,30 55,30 55,45" 
                    fill="none" 
                    stroke="#D4AF37" 
                    strokeWidth="2"
                  />
                  
                  {/* Data/sparkle trail */}
                  <g className="data-trail">
                    <circle cx="50" cy="25" r="2" fill="#D4AF37" className="animate-pulse" />
                    <circle cx="45" cy="20" r="1.5" fill="#00BFFF" className="animate-pulse" />
                    <circle cx="55" cy="18" r="1.5" fill="#D4AF37" className="animate-pulse" />
                    <circle cx="48" cy="15" r="1" fill="#00BFFF" className="animate-pulse" />
                    <circle cx="52" cy="10" r="1" fill="#D4AF37" className="animate-pulse" />
                  </g>
                </svg>
              </div>
            </div>
            <h2 className="text-2xl font-bold text-midnight-navy mb-2">Welcome to Research Genie</h2>
            <p className="text-gray-600 mb-6">Ready to make your first wish?</p>
            <button 
              onClick={handleNext}
              className="w-full bg-pale-gold text-midnight-navy font-semibold py-3 px-4 rounded-md hover:bg-opacity-90 transition-colors"
            >
              Let's Go
            </button>
          </div>
        )}
        
        {step === 2 && (
          <div>
            <h2 className="text-xl font-bold text-midnight-navy mb-4">Summon Your Genie</h2>
            <div className="mb-4">
              <label className="block text-gray-700 mb-2">Pick Your Focus</label>
              <select 
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pale-gold"
                value={focus}
                onChange={(e) => setFocus(e.target.value)}
              >
                <option value="">Select focus...</option>
                <option value="public-equities">Public Equities</option>
                <option value="etfs">ETFs</option>
                <option value="sectors">Sectors</option>
                <option value="macros">Macros</option>
              </select>
            </div>
            
            <div className="mb-6">
              <label className="block text-gray-700 mb-2">Choose Your First Company</label>
              <input 
                type="text" 
                placeholder="Enter ticker or company name"
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pale-gold"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
              />
              <p className="text-sm text-gray-500 mt-1">Pick a company, and we'll show you the magic.</p>
            </div>
            
            <button 
              onClick={handleNext}
              className="w-full bg-pale-gold text-midnight-navy font-semibold py-3 px-4 rounded-md hover:bg-opacity-90 transition-colors"
              disabled={!focus || !company}
            >
              Continue
            </button>
          </div>
        )}
        
        {step === 3 && (
          <div>
            <div className="flex items-center mb-4">
              <div className="text-pale-gold text-2xl mr-3">✨</div>
              <h2 className="text-xl font-bold text-midnight-navy">Making your first wish...</h2>
            </div>
            
            <div className="bg-gray-50 p-4 rounded-md mb-6">
              <div className="animate-pulse flex space-x-4">
                <div className="flex-1 space-y-4 py-1">
                  <div className="h-4 bg-gray-300 rounded w-3/4"></div>
                  <div className="space-y-2">
                    <div className="h-4 bg-gray-300 rounded"></div>
                    <div className="h-4 bg-gray-300 rounded w-5/6"></div>
                  </div>
                </div>
              </div>
            </div>
            
            <button 
              onClick={handleNext}
              className="w-full bg-pale-gold text-midnight-navy font-semibold py-3 px-4 rounded-md hover:bg-opacity-90 transition-colors"
            >
              Show Results
            </button>
          </div>
        )}
        
        {step === 4 && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-midnight-navy">Your first research wish granted!</h2>
              <div className="text-pale-gold text-2xl">🔮</div>
            </div>
            
            <div className="bg-gray-50 p-4 rounded-md mb-6">
              <h3 className="font-semibold mb-2">{company || 'AAPL'} Analysis</h3>
              <div className="grid grid-cols-2 gap-2 mb-4">
                <div className="bg-white p-2 rounded shadow-sm">
                  <div className="text-xs text-gray-500">P/E Ratio</div>
                  <div className="font-mono font-medium">28.5</div>
                </div>
                <div className="bg-white p-2 rounded shadow-sm">
                  <div className="text-xs text-gray-500">EPS</div>
                  <div className="font-mono font-medium">$6.14</div>
                </div>
                <div className="bg-white p-2 rounded shadow-sm">
                  <div className="text-xs text-gray-500">Revenue</div>
                  <div className="font-mono font-medium">$394.3B</div>
                </div>
                <div className="bg-white p-2 rounded shadow-sm">
                  <div className="text-xs text-gray-500">Market Cap</div>
                  <div className="font-mono font-medium">$2.8T</div>
                </div>
              </div>
              
              <div className="mb-4">
                <div className="text-sm font-semibold mb-1">Key Risks:</div>
                <ul className="text-sm text-gray-700 list-disc pl-5">
                  <li>Supply chain disruptions</li>
                  <li>Increasing competition</li>
                  <li>Regulatory challenges</li>
                </ul>
              </div>
              
              <div className="text-sm italic border-t pt-2">
                "{company || 'AAPL'} shows strong fundamentals with growth potential in emerging markets, despite facing increased competitive pressure."
              </div>
            </div>
            
            <div className="flex space-x-3">
              <button 
                className="flex-1 bg-midnight-navy text-white font-semibold py-2 px-4 rounded-md hover:bg-opacity-90 transition-colors"
              >
                Explore Agents
              </button>
              <button 
                className="flex-1 bg-midnight-navy text-white font-semibold py-2 px-4 rounded-md hover:bg-opacity-90 transition-colors"
              >
                Save Report
              </button>
              <button 
                onClick={onClose}
                className="flex-1 bg-midnight-navy text-white font-semibold py-2 px-4 rounded-md hover:bg-opacity-90 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default OnboardingFlow;
